/**
 * Defensive guard against the React + browser-translation DOM crash.
 *
 * Problem (root-caused via Sentry issue TM-FRONTEND-2 / NotFoundError:
 * "Failed to execute 'removeChild' on 'Node'"):
 * When Chrome's built-in auto-translation (or an equivalent extension) is active,
 * it rewrites text nodes into <font> wrappers BEHIND React's back. When React
 * later reconciles — e.g. a conditional render on the welcome page after a click
 * (button.welcome-button -> filteredResidence) — it calls removeChild/insertBefore
 * on a node Translate has already moved or replaced. The native DOM call throws,
 * the exception bubbles to the root ErrorBoundary, and the user is stuck on the
 * "Oups" page until they reload without translation (e.g. private browsing).
 *
 * This is a LATENT bug: the crashing conditional render exists since the Italian
 * welcome page (commit 44ba944) and no translation guard ever shipped. It is
 * triggered by the residence's browser environment (auto-translate on), not by
 * any code change.
 *
 * Fix: make removeChild/insertBefore non-throwing ONLY in the exact Translate
 * case — when the target node is genuinely not where the DOM expects it
 * (child.parentNode !== this). Every other call delegates to native behaviour
 * unchanged. Translation keeps working; only the crash is neutralised.
 * Battle-tested mitigation from facebook/react#11538.
 *
 * Visibility trade-off (deliberate): a non-throwing removeChild could otherwise
 * mask a genuine React reconciliation bug. So we DON'T swallow blindly — when an
 * interception happens we check whether the page is actually translated:
 *   - translated  -> expected case, light Sentry breadcrumb (frequency only).
 *   - NOT translated -> unexpected; we still avoid the crash but captureMessage()
 *     so a real DOM bug stays visible in Sentry instead of going silent.
 *
 * Must run BEFORE ReactDOM.render so the patched methods are in place before the
 * first reconciliation.
 */
import * as Sentry from "@sentry/react";

function isPageTranslated(): boolean {
  // Google Translate tags <html> with these classes when it rewrites the DOM.
  const el = typeof document !== "undefined" ? document.documentElement : null;
  return (
    !!el &&
    (el.classList.contains("translated-ltr") || el.classList.contains("translated-rtl"))
  );
}

function reportInterception(op: "removeChild" | "insertBefore"): void {
  if (isPageTranslated()) {
    Sentry.addBreadcrumb({
      category: "translate-dom-guard",
      message: `${op} intercepted on a translated page`,
      level: "info",
    });
  } else {
    // Prod verdict (TM-FRONTEND-28, 953 events / 628 users in 7 days): the
    // NON-translated interceptions are overwhelmingly the Agora SDK's own
    // video-element cleanup racing React's unmount — the SDK appends <video>
    // elements inside React-managed containers on track.play(domId) and removes
    // them itself on track.stop(); React's cleanup then removes the same node
    // again. BENIGN and expected (and pre-guard it crashed to the "Oups"
    // ErrorBoundary — the removeChild crash class was always broader than
    // Google Translate). So: breadcrumb always, and keep only a ~1% sampled
    // captureMessage for visibility into rarer genuine cases.
    Sentry.addBreadcrumb({
      category: "translate-dom-guard",
      message: `${op} intercepted on a NON-translated page (Agora element-cleanup race)`,
      level: "warning",
    });
    if (Math.random() < 0.01) {
      Sentry.captureMessage(
        `[translate-dom-guard] ${op} intercepted on a NON-translated page (1% sample)`,
        "warning"
      );
    }
  }
}

export function installTranslateDomGuard(): void {
  if (typeof Node !== "function" || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportInterception("removeChild");
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      reportInterception("insertBefore");
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  } as typeof Node.prototype.insertBefore;
}
